import React from "react";

/**
 * seGuru symbol.
 *
 * Stroke is responsive by rendered size to keep optical weight consistent:
 *   ≤  32px → 2.00px   (favicon, icon)
 *   ≤  64px → 2.25px   (UI — base)
 *   ≤ 128px → 2.50px   (header, hero)
 *   >  128px → 2.75px   (display, signage)
 *
 * Override the stroke explicitly via `strokePx` when needed.
 */
export type SeguruSymbolProps = {
  size?: number;          // rendered px (square). default 64
  strokePx?: number;      // override responsive rule
  ink?: string;           // outer stroke color. default #0B0B0E
  accent?: string;        // inner mark color. default #C06A3E
  title?: string;         // a11y label. default "seGuru"
  className?: string;
  style?: React.CSSProperties;
};

const INK = "#0B0B0E";
const TERRA = "#C06A3E";

export function strokeForSize(size: number): number {
  if (size <= 32)  return 2.00;
  if (size <= 64)  return 2.25;
  if (size <= 128) return 2.50;
  return 2.75;
}

export function SeguruSymbol({
  size = 64,
  strokePx,
  ink = INK,
  accent = TERRA,
  title = "seGuru",
  className,
  style,
}: SeguruSymbolProps) {
  const px = strokePx ?? strokeForSize(size);
  // viewBox is 100x100 — convert px → viewBox units so the stroke is exact at any size.
  const sw = (px / size) * 100;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={title}
      className={className}
      style={style}
    >
      <title>{title}</title>
      <path
        d="M 14 14 L 86 14 L 86 62 Q 86 86 62 86 L 14 86 Z"
        fill="none"
        stroke={ink}
        strokeWidth={sw}
        strokeLinejoin="round"
      />
      <circle cx="34" cy="34" r="16" fill="none" stroke={accent} strokeWidth={sw * 0.85} />
      <circle cx="34" cy="34" r="4" fill={accent} />
    </svg>
  );
}

export default SeguruSymbol;
